import { uiLabels } from '@menage/ui';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { storage } from '../lib/storage';
import enAdmin from './en/admin';
import enAuth from './en/auth';
import enAdminCrm from './en/adminCrm';
import enAdminHr from './en/adminHr';
import enAdminOps from './en/adminOps';
import enChat from './en/chat';
import enClient from './en/client';
import enCommon from './en/common';
import enEmployee from './en/employee';
import enPayouts from './en/payouts';
import enPublic from './en/public';
import enSite from './en/site';
import frAdmin from './fr/admin';
import frAuth from './fr/auth';
import frAdminCrm from './fr/adminCrm';
import frAdminHr from './fr/adminHr';
import frAdminOps from './fr/adminOps';
import frChat from './fr/chat';
import frClient from './fr/client';
import frCommon from './fr/common';
import frEmployee from './fr/employee';
import frPayouts from './fr/payouts';
import frPublic from './fr/public';
import frSite from './fr/site';

/**
 * i18n from day one: French by default, English available. One namespace per area,
 * and every English file is typed against the French one (compile-time key parity).
 */
export const resources = {
  fr: { auth: frAuth, common: frCommon, public: frPublic, client: frClient, employee: frEmployee, admin: frAdmin, adminOps: frAdminOps, adminCrm: frAdminCrm, adminHr: frAdminHr, chat: frChat, payouts: frPayouts, site: frSite },
  en: { auth: enAuth, common: enCommon, public: enPublic, client: enClient, employee: enEmployee, admin: enAdmin, adminOps: enAdminOps, adminCrm: enAdminCrm, adminHr: enAdminHr, chat: enChat, payouts: enPayouts, site: enSite },
} as const;

export type Lang = 'fr' | 'en';
const LANG_KEY = 'menage.lang';
/** Shared key used by every Nexora Creation demo. */
const NX_LANG_KEY = 'nx-lang';

const asLang = (v: string | null | undefined): Lang | null => (v === 'en' || v === 'fr' ? v : null);

function readCookie(name: string) {
  try {
    return document.cookie.split('; ').find((c) => c.startsWith(`${name}=`))?.split('=')[1] ?? null;
  } catch {
    return null;
  }
}

/**
 * Language at startup, English first: ?lang=en|fr (then remembered), the
 * shared nx-lang key (localStorage, or a cookie set by a server-rendered
 * page), the app's own menage.lang key, then English. The browser language
 * is deliberately not used.
 */
export function initialLanguage(): Lang {
  const fromUrl = typeof window !== 'undefined' ? asLang(new URLSearchParams(window.location.search).get('lang')) : null;
  if (fromUrl) {
    rememberLanguage(fromUrl);
    return fromUrl;
  }
  return asLang(storage.get(NX_LANG_KEY)) ?? asLang(readCookie(NX_LANG_KEY)) ?? asLang(storage.get(LANG_KEY)) ?? 'en';
}

function rememberLanguage(lang: Lang) {
  storage.set(LANG_KEY, lang);
  storage.set(NX_LANG_KEY, lang);
  try {
    document.cookie = `${NX_LANG_KEY}=${lang}; path=/; max-age=31536000; SameSite=Lax`;
  } catch {
    /* cookies disabled: localStorage still remembers */
  }
}

function syncUiLabels(lang: Lang) {
  uiLabels.close = lang === 'en' ? 'Close' : 'Fermer';
}

export function setLanguage(lang: Lang) {
  syncUiLabels(lang);
  rememberLanguage(lang);
  document.documentElement.lang = lang;
  void i18n.changeLanguage(lang);
}

const startLang = initialLanguage();
syncUiLabels(startLang);
if (typeof document !== 'undefined') document.documentElement.lang = startLang;

void i18n.use(initReactI18next).init({
  resources,
  lng: startLang,
  fallbackLng: 'en',
  defaultNS: 'common',
  ns: ['auth', 'common', 'public', 'client', 'employee', 'admin', 'adminOps', 'adminCrm', 'adminHr', 'chat', 'payouts', 'site'],
  interpolation: { escapeValue: false },
  returnNull: false,
});

export default i18n;
